import React from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatToPKR, formatChartValue, formatChartTooltip } from "@/lib/helper";
import { Transaction } from "@/types/pos/transaction";
import { SaleStatus } from "@/types/enums";

interface SalesChartsProps {
  data: Transaction[];
  isLoading: boolean;
  filters?: {
    start_date?: string;
    end_date?: string;
    income_category_ids?: (string | number)[];
    payment_method_ids?: (string | number)[];
    counter_ids?: (string | number)[];
    status?: string;
    member_id?: string;
    erp_status?: string;
  };
  dateRange?: {
    start_date?: string;
    end_date?: string;
  };
}

interface ChartData {
  name: string;
  sales: number;
  refunds: number;
  netRevenue: number;
}

interface PieData {
  name: string;
  value: number;
  color: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

// Generate unique colors for pie charts
const generateUniqueColors = (count: number): string[] => {
  const colors: string[] = [];
  const usedColors = new Set<string>();
  
  // Base palette for good contrast (no duplicates)
  const basePalette = [
    '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d',
    '#FF6B9D', '#C44569', '#F8B500', '#6C5CE7', '#00D2D3', '#FF6348',
    '#5F27CD', '#00D2FF', '#FF9FF3', '#54A0FF', '#10AC84', '#EE5A6F',
    '#A55EEA', '#26DE81', '#FED330', '#FC5C65', '#45AAF2', '#FD79A8',
    '#FDCB6E', '#6C5CE7', '#A29BFE', '#FD79A8', '#FDCB6E', '#E17055'
  ];
  
  // Remove duplicates from base palette
  const uniqueBasePalette = Array.from(new Set(basePalette));
  
  // First, use base palette colors
  for (let i = 0; i < Math.min(count, uniqueBasePalette.length); i++) {
    colors.push(uniqueBasePalette[i]);
    usedColors.add(uniqueBasePalette[i]);
  }
  
  // Helper function to convert HSL to hex
  const hslToHex = (h: number, s: number, l: number): string => {
    l /= 100;
    const a = (s * Math.min(l, 1 - l)) / 100;
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  };
  
  // If we need more colors, generate them using HSL
  if (count > uniqueBasePalette.length) {
    for (let i = uniqueBasePalette.length; i < count; i++) {
      let color: string;
      let attempts = 0;
      
      // Generate colors with good saturation and lightness for visibility
      do {
        const hue = (i * 137.508) % 360; // Golden angle approximation for good distribution
        const saturation = 60 + (i % 3) * 10; // Vary between 60-80%
        const lightness = 45 + (i % 2) * 10; // Vary between 45-55%
        color = hslToHex(hue, saturation, lightness);
        attempts++;
      } while (usedColors.has(color) && attempts < 100);
      
      colors.push(color);
      usedColors.add(color);
    }
  }
  
  return colors;
};

export default function SalesCharts({ data, isLoading, filters, dateRange }: SalesChartsProps) {
  const processDataForCharts = () => {
    if (!data || data.length === 0) return { 
      monthlyData: [], 
      statusData: [], 
      paymentMethodData: [], 
      incomeCategoryData: [],
      paymentMethodTrendData: [],
      incomeCategoryTrendData: []
    };

    // Group by month for line/bar charts
    const monthlyGroups = data.reduce((acc, item) => {
      const date = new Date(item.created_at || '');
      const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      
      if (!acc[monthKey]) {
        acc[monthKey] = { sales: 0, refunds: 0 };
      }

      if (item.transaction_type === SaleStatus.Sale) {
        acc[monthKey].sales += item.total || 0;
      } else if (item.transaction_type === SaleStatus.Refund) {
        acc[monthKey].refunds += item.total || 0;
      }

      return acc;
    }, {} as Record<string, { sales: number; refunds: number }>);

    const monthlyData: ChartData[] = Object.entries(monthlyGroups).map(([month, values]) => ({
      name: month,
      sales: values.sales,
      refunds: values.refunds,
      netRevenue: values.sales - values.refunds,
    }));

    // Group by status for pie chart
    const statusGroups = data.reduce((acc, item) => {
      const status = item.status || 'Unknown';
      acc[status] = (acc[status] || 0) + (item.total || 0);
      return acc;
    }, {} as Record<string, number>);

    const statusData: PieData[] = Object.entries(statusGroups).map(([status, value], index) => ({
      name: status,
      value,
      color: COLORS[index % COLORS.length],
    }));

    // Group by payment method
    const paymentMethodGroups = data.reduce((acc, item) => {
      if (item.payments && item.payments.length > 0) {
        item.payments.forEach(payment => {
          const method = payment.payment_method || 'Unknown';
          acc[method] = (acc[method] || 0) + (payment.amount || 0);
        });
      }
      return acc;
    }, {} as Record<string, number>);

    const paymentMethodEntries = Object.entries(paymentMethodGroups);
    const paymentMethodColors = generateUniqueColors(paymentMethodEntries.length);
    const paymentMethodData: PieData[] = paymentMethodEntries.map(([method, value], index) => ({
      name: method,
      value,
      color: paymentMethodColors[index],
    }));

    // Group by income category
    const incomeCategoryGroups = data.reduce((acc, item) => {
      if (item.items && item.items.length > 0) {
        item.items.forEach(saleItem => {
          const category = saleItem.income_category_name || 'Unknown';
          acc[category] = (acc[category] || 0) + (saleItem.total || 0);
        });
      }
      return acc;
    }, {} as Record<string, number>);

    const incomeCategoryEntries = Object.entries(incomeCategoryGroups);
    const incomeCategoryColors = generateUniqueColors(incomeCategoryEntries.length);
    const incomeCategoryData: PieData[] = incomeCategoryEntries.map(([category, value], index) => ({
      name: category,
      value,
      color: incomeCategoryColors[index],
    }));

    // Payment method trend by month
    const paymentMethodTrendGroups = data.reduce((acc, item) => {
      const date = new Date(item.created_at || '');
      const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      
      if (!acc[monthKey]) {
        acc[monthKey] = {};
      }

      if (item.payments && item.payments.length > 0) {
        item.payments.forEach(payment => {
          const method = payment.payment_method || 'Unknown';
          acc[monthKey][method] = (acc[monthKey][method] || 0) + (payment.amount || 0);
        });
      }

      return acc;
    }, {} as Record<string, Record<string, number>>);

    const paymentMethods = Array.from(new Set(data.flatMap(item => 
      item.payments?.map(p => p.payment_method) || []
    )));

    const paymentMethodTrendData = Object.entries(paymentMethodTrendGroups).map(([month, methods]) => ({
      name: month,
      ...methods
    }));

    // Income category trend by month
    const incomeCategoryTrendGroups = data.reduce((acc, item) => {
      const date = new Date(item.created_at || '');
      const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      
      if (!acc[monthKey]) {
        acc[monthKey] = {};
      }

      if (item.items && item.items.length > 0) {
        item.items.forEach(saleItem => {
          const category = saleItem.income_category_name || 'Unknown';
          acc[monthKey][category] = (acc[monthKey][category] || 0) + (saleItem.total || 0);
        });
      }

      return acc;
    }, {} as Record<string, Record<string, number>>);

    const incomeCategories = Array.from(new Set(data.flatMap(item => 
      item.items?.map(i => i.income_category_name) || []
    )));

    const incomeCategoryTrendData = Object.entries(incomeCategoryTrendGroups).map(([month, categories]) => ({
      name: month,
      ...categories
    }));

    return { 
      monthlyData, 
      statusData, 
      paymentMethodData, 
      incomeCategoryData,
      paymentMethodTrendData,
      incomeCategoryTrendData,
      paymentMethods,
      incomeCategories
    };
  };

  const { 
    monthlyData, 
    statusData, 
    paymentMethodData, 
    incomeCategoryData,
    paymentMethodTrendData,
    incomeCategoryTrendData,
    paymentMethods,
    incomeCategories
  } = processDataForCharts();

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="font-medium">{`${label}`}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.dataKey}: ${formatChartTooltip(entry.value)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="font-medium">{payload[0].name}</p>
          <p style={{ color: payload[0].payload.color }}>
            {`Value: ${formatChartTooltip(payload[0].value)}`}
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ data }: { data: PieData[] }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    
    return (
      <div className="flex flex-col gap-2 h-full max-h-[300px] custom-scrollbar overflow-y-auto pr-2">
        {data.map((item, index) => {
          const percent = total > 0 ? ((item.value / total) * 100).toFixed(1) : '0';
          return (
            <div key={index} className="flex items-center gap-2 text-sm shrink-0">
              <div
                className="w-4 h-4 rounded shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{item.name}</div>
                <div className="text-xs text-muted-foreground">
                  {formatChartTooltip(item.value)} ({percent}%)
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-6 bg-gray-200 rounded w-32"></div>
            </CardHeader>
            <CardContent>
              <div className="h-64 bg-gray-200 rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Monthly Sales Trend - Line Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="fa-solid fa-chart-line text-blue-600"></i>
            Monthly Sales Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={(value) => formatChartValue(value)} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="sales" 
                stroke="#8884d8" 
                strokeWidth={2}
                name="Sales"
              />
              <Line 
                type="monotone" 
                dataKey="refunds" 
                stroke="#ff7300" 
                strokeWidth={2}
                name="Refunds"
              />
              <Line 
                type="monotone" 
                dataKey="netRevenue" 
                stroke="#00C49F" 
                strokeWidth={2}
                name="Net Revenue"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Sales vs Refunds - Bar Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="fa-solid fa-chart-bar text-green-600"></i>
            Sales vs Refunds
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={(value) => formatChartValue(value)} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="sales" fill="#8884d8" name="Sales" />
              <Bar dataKey="refunds" fill="#ff7300" name="Refunds" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Income Category Distribution - Pie Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="fa-solid fa-tags text-purple-600"></i>
            Income Category Distribution
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-center h-[300px]">
            <div className="flex-1 h-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={incomeCategoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {incomeCategoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {incomeCategoryData.length > 0 && (
              <div className="w-48 shrink-0">
                <CustomLegend data={incomeCategoryData} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payment Methods Distribution - Pie Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="fa-solid fa-credit-card text-orange-600"></i>
            Payment Methods Distribution
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-center h-[300px]">
            <div className="flex-1 h-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={paymentMethodData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {paymentMethodData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {paymentMethodData.length > 0 && (
              <div className="w-48 shrink-0">
                <CustomLegend data={paymentMethodData} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Income Category Trend - Bar Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="fa-solid fa-chart-column text-indigo-600"></i>
            Income Category Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={incomeCategoryTrendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={(value) => formatChartValue(value)} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {incomeCategories?.filter(Boolean).slice(0, 5).map((category, index) => (
                <Bar 
                  key={category} 
                  dataKey={category!} 
                  fill={COLORS[index % COLORS.length]} 
                  name={category}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Payment Method Trend - Line Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="fa-solid fa-chart-area text-teal-600"></i>
            Payment Method Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={paymentMethodTrendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={(value) => formatChartValue(value)} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {paymentMethods?.filter(Boolean).slice(0, 5).map((method, index) => (
                <Line 
                  key={method}
                  type="monotone" 
                  dataKey={method!} 
                  stroke={COLORS[index % COLORS.length]} 
                  strokeWidth={2}
                  name={method}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
